import React, { useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Rate, Spin, Table, Typography, message } from 'antd';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import customerLifecycleService from '@/services/customerLifecycle.service';

const { Title, Paragraph } = Typography;

const CustomerPortalPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['customer-portal', token],
    queryFn: () => customerLifecycleService.getPortalWorkspace(token!),
    enabled: !!token,
  });

  const submitSurvey = async (surveyId: string) => {
    if (!token) return;
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      await customerLifecycleService.submitPortalSurvey(token, surveyId, {
        nps_score: values.nps_score,
        csat_score: values.csat_score,
        feedback_text: values.feedback_text,
      });
      message.success('Thank you for your feedback');
      refetch();
    } finally {
      setSubmitting(false);
    }
  };

  const customer = data?.customer as { customer_name?: string } | undefined;
  const pending = (data?.pending_surveys as { id: string; survey_type: string }[]) ?? [];

  return (
    <div style={{ maxWidth: 900, margin: '40px auto', padding: 24 }}>
      <Spin spinning={isLoading}>
        <Title level={2}>Customer Portal</Title>
        <Paragraph>Welcome, {customer?.customer_name ?? 'Customer'}</Paragraph>

        <Card title="Invoices" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={(data?.invoices as unknown[]) ?? []}
            columns={[
              { title: 'Invoice', dataIndex: 'invoice_number' },
              { title: 'Date', dataIndex: 'invoice_date' },
              { title: 'Status', dataIndex: 'payment_status' },
              { title: 'Amount', dataIndex: 'grand_total' },
            ]}
          />
        </Card>

        <Card title="Deliveries" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={(data?.deliveries as unknown[]) ?? []}
            columns={[
              { title: 'Delivery', dataIndex: 'delivery_note_number' },
              { title: 'Date', dataIndex: 'delivery_date' },
              { title: 'Status', dataIndex: 'delivery_status' },
            ]}
          />
        </Card>

        {pending.length > 0 && (
          <Card title="We value your feedback">
            <Form form={form} layout="vertical">
              <Form.Item name="nps_score" label="How likely are you to recommend us? (0-10)" rules={[{ required: true }]}>
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="csat_score" label="Overall satisfaction">
                <Rate count={5} />
              </Form.Item>
              <Form.Item name="feedback_text" label="Comments">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Button type="primary" loading={submitting} onClick={() => submitSurvey(pending[0].id)}>
                Submit feedback
              </Button>
            </Form>
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default CustomerPortalPage;
